import { useState, useCallback } from 'react';
import { APP_CONFIG } from '../config/appConfig';
import { authFetch } from '../config/authConfig';

const API = APP_CONFIG.BACKEND_URL;

export interface ConversationRatings {
  session: { score: number; comentario: string | null } | null;
  responses: Record<number, number>;
}

export function useRatings(userId: string) {
  const [ratings, setRatings] = useState<ConversationRatings>({ session: null, responses: {} });
  const [savingRating, setSavingRating] = useState(false);

  const loadRatings = useCallback(async (conversationId: string) => {
    try {
      const res = await authFetch(`${API}/api/v1/ratings/conversation/${conversationId}`);
      if (res.ok) {
        const data = await res.json();
        setRatings({
          session: data.session ? { score: data.session.score, comentario: data.session.comentario } : null,
          responses: data.responses || {},
        });
      }
    } catch (err) {
      console.error('Error loading ratings:', err);
    }
  }, []);

  const rateResponse = useCallback(async (conversationId: string, messageId: number, score: number) => {
    try {
      const res = await authFetch(`${API}/api/v1/ratings/response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversation_id: conversationId, message_id: messageId, user_id: userId, score }),
      });
      if (res.ok) {
        setRatings(prev => ({
          ...prev,
          responses: { ...prev.responses, [messageId]: score },
        }));
      }
    } catch (err) {
      console.error('Error rating response:', err);
    }
  }, [userId]);

  const rateSession = useCallback(async (conversationId: string, score: number, comentario?: string) => {
    setSavingRating(true);
    try {
      const res = await authFetch(`${API}/api/v1/ratings/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversation_id: conversationId, user_id: userId, score, comentario: comentario || null }),
      });
      if (res.ok) {
        setRatings(prev => ({
          ...prev,
          session: { score, comentario: comentario || null },
        }));
      }
    } catch (err) {
      console.error('Error rating session:', err);
    } finally {
      setSavingRating(false);
    }
  }, [userId]);

  const resetRatings = useCallback(() => {
    setRatings({ session: null, responses: {} });
  }, []);

  return {
    ratings,
    savingRating,
    loadRatings,
    rateResponse,
    rateSession,
    resetRatings,
  };
}
